import axios from 'axios';
import { forEach, some } from 'lodash';
import { runInAction, makeAutoObservable } from 'mobx';
import moment from 'moment';

export enum meetingStatus {
  'SCHEDULED',
  'DONE',
  'CANCELED',
  'NO SHOW',
}

export interface IRevision {
  userUuid: string;
  note: string;
  uuid: string;
  createdAt: string;
  clinicalDocumentUuid: string;
  therapistUuid: string;
}

export interface IMeeting {
  location: string;
  meetingTime: string;
  note: string;
  patientUuid: string;
  status: meetingStatus;
  timestamp: string;
  userUuid: string;
  uuid: string;
  soap: any; // eslint-disable-line
  revisions: IRevision[];
  link?: string;
}

export interface IMeetingsStore {
  fetchPatientMeetings(patientUuid: string): Promise<IMeeting[]>;
  fetchLoggedUserPatientsFututeMeetings(): Promise<void>;
  getCurrentMeeting(meetingUuid: string): Promise<IMeeting>;
  addRevision(
    meetingUuid: string,
    soap: any, // eslint-disable-line
    revisionData: { note: string },
    fileUuid: string
  ): Promise<void>;
  updateMeeting(
    meetingUuid: string,
    patientUuid: string,
    changes: any // eslint-disable-line
  ): Promise<void>;
  meetings: { [key: string]: IMeeting[] };
  futureMeetingsLoading: boolean;
}

export default class MeetingsStore implements IMeetingsStore {
  meetings = {} as { [key: string]: IMeeting[] };
  futureMeetingsLoading = true;
  constructor() {
    makeAutoObservable(this);
  }

  async fetchPatientMeetings(patientUuid: string) {
    const result = await axios.get(
      `/api/patients/${patientUuid}/meeting`
    );

    runInAction(() => {
      this.meetings[patientUuid] = result?.data?.result;
    });

    return result?.data?.result;
  }

  async fetchLoggedUserPatientsFututeMeetings() {
    this.futureMeetingsLoading = true;
    const nowDate = moment().format('YYYY-MM-DD');
    const futureDate = moment().add(30, 'years').format('YYYY-MM-DD');
    const result = await axios.get(
      `/api/meeting/list/?myPatients=true&fromDate=${nowDate}&untilDate=${futureDate}`
    );

    runInAction(() => {
      forEach(result?.data?.result, (meeting) => {
        if (!this.meetings[meeting.patientUuid]) {
          this.meetings[meeting.patientUuid] = [];
          this.meetings[meeting.patientUuid].push(meeting);
        } else if (
          !some(
            this.meetings[meeting.patientUuid],
            (x) => x.uuid === meeting.uuid
          )
        ) {
          this.meetings[meeting.patientUuid].push(meeting);
        }
      });
      this.futureMeetingsLoading = false;
    });
  }

  async getCurrentMeeting(meetingUuid: string) {
    const result = await axios.get(`/api/meeting/${meetingUuid}`);
    return result?.data as IMeeting;
  }

  async addRevision(
    meetingUuid: string,
    soap: any, // eslint-disable-line
    revisionData: any, // eslint-disable-line
    fileUuid: string
  ) {
    await axios.post(
      `/api/meeting/${meetingUuid}/revision`,
      { soap: soap, ...revisionData, clinicalDocumentUuid: fileUuid }
    );
  }

  async updateMeeting(meetingUuid: string, patientUuid: string, changes: any) { // eslint-disable-line
    const result = await axios.put(
      `/api/meeting/${meetingUuid}`,
      changes
    );

    runInAction(() => {
      this.meetings[patientUuid] = this.meetings[patientUuid].map((x) =>
        x.uuid === meetingUuid ? result?.data : x
      );
    });
  }
}
